/**
 * PATHWAY-4098 — Amplitude ownership when the native app hosts the web SPA in a WebView.
 *
 * Native injects `window.amplitudeDeviceId` / `window.amplitudeSessionId`; the browser SDK
 * runs a second client in the same device/session. Autocapture on both sides would double-count
 * taps; manual screen/page events would overlap native `ViewScreen`; explore documents would
 * double-fire `ResourceView` (native doc shell + web `DocumentRouteHandler`).
 *
 * | Signal | Native-hosted WebView (`window.native.isNative`) | Standalone web |
 * |--------|--------------------------------------------------|----------------|
 * | Autocapture element interactions | Native (RN + `ampCapture` / Babel) | Web browser SDK |
 * | Page / screen impressions | Native `ViewScreen` (not web page views) | Web autocapture page views + manual events |
 * | `ResourceView` on explore `/document` routes (not compliance) | Native explore doc screen | Web `DocumentRouteHandler` |
 * | `ResourceView` on compliance `/compliance/doc/…` and plan proxy routes | Web (native does not emit) | Web |
 * | Manual `track()` / `UserAction` in WebView | Web (business events) | Web |
 * | Session start/end | Native `trackingSessionEvents`; web sessions off in WebView | Web / shared policy per 4097 |
 * | Network autocapture | Off (Datadog RUM) | Off (Datadog RUM) |
 *
 * Runtime ownership guards (web init + explore ResourceView skip) apply only when LaunchDarkly
 * `amplitude-autocapture` is on — until migration, legacy WebView analytics behavior is unchanged.
 */

export interface WebDefaultTrackingFlags {
  sessions: boolean;
  pageViews: boolean;
  formInteractions: boolean;
  fileDownloads: boolean;
  attribution: boolean;
}

/** Browser SDK `defaultTracking` for embedded web when sharing native device/session IDs. */
export const WEB_NATIVE_HOSTED_DEFAULT_TRACKING: WebDefaultTrackingFlags = {
  sessions: false,
  pageViews: false,
  formInteractions: false,
  fileDownloads: false,
  attribution: false,
};

/** Shared LD key for PATHWAY-4097 / 4098 / 4099 autocapture rollout (default off). */
export const AMPLITUDE_AUTOCAPTURE_LAUNCH_DARKLY_FLAG = 'amplitude-autocapture';

/** Pre-migration browser `defaultTracking` when sharing native device/session IDs (flag off). */
export const WEB_LEGACY_NATIVE_HOSTED_DEFAULT_TRACKING: WebDefaultTrackingFlags =
  {
    sessions: false,
    pageViews: true,
    formInteractions: false,
    fileDownloads: false,
    attribution: false,
  };

export interface WebAutocaptureOptions {
  pageViews?: boolean;
  elementInteractions?: boolean;
  sessions?: boolean;
  formInteractions?: boolean;
  fileDownloads?: boolean;
  attribution?: boolean;
  networkTracking?: boolean | Record<string, unknown>;
}

export const isNativeHostedWebView = (isNative: boolean): boolean => isNative;

/**
 * Browser SDK init overrides whenever `window.native.isNative` is true.
 * Valid injected device/session IDs are merged when present; tracking guards apply
 * even when injection is missing (init may still use standalone `defaultTracking`).
 */
export const buildWebBrowserInitForNativeHostedWebView = (ids?: {
  deviceId: string;
  sessionId: number;
}): {
  deviceId?: string;
  sessionId?: number;
  defaultTracking: WebDefaultTrackingFlags;
} => ({
  ...(ids ?? {}),
  defaultTracking: WEB_NATIVE_HOSTED_DEFAULT_TRACKING,
});

/**
 * Web Amplitude `init` overrides for native-hosted WebView — respects LD before applying 4098 guards.
 */
export const resolveWebBrowserInitForNativeHostedWebView = ({
  nativeIds,
  amplitudeAutocaptureEnabled,
}: {
  nativeIds?: { deviceId: string; sessionId: number };
  amplitudeAutocaptureEnabled: boolean;
}):
  | {
      deviceId?: string;
      sessionId?: number;
      defaultTracking?: WebDefaultTrackingFlags;
    }
  | undefined => {
  if (!amplitudeAutocaptureEnabled) {
    if (!nativeIds) {
      return undefined;
    }
    return {
      ...nativeIds,
      defaultTracking: WEB_LEGACY_NATIVE_HOSTED_DEFAULT_TRACKING,
    };
  }
  return buildWebBrowserInitForNativeHostedWebView(nativeIds);
};

/**
 * PATHWAY-4097: merge full web `autocapture` config with WebView guards so embedded web
 * never enables dimensions owned by native.
 */
export const applyNativeWebViewAutocaptureGuards = <
  T extends WebAutocaptureOptions,
>(
  autocapture: T,
  isNativeWebView: boolean,
  amplitudeAutocaptureEnabled = false,
): T => {
  if (!isNativeWebView || !amplitudeAutocaptureEnabled) {
    return autocapture;
  }
  return {
    ...autocapture,
    pageViews: false,
    elementInteractions: false,
    sessions: false,
    networkTracking: false,
  };
};

/**
 * Whether web should emit `ResourceView` from `DocumentRouteHandler`.
 * Native explore doc shell owns explore `/doc/:id` views; compliance documents in
 * Plans/Reports WebViews remain web-owned.
 *
 * TODO(PATHWAY-4099): When explore is served via web fallback (`native-explore-web-view`),
 * native does not emit ResourceView — thread explicit shell context from native injection and
 * only suppress when the native explore doc screen owns the event.
 */
export const shouldWebTrackExploreResourceView = (
  isNativeWebView: boolean,
  options?: {
    isComplianceDocument?: boolean;
    amplitudeAutocaptureEnabled?: boolean;
  },
): boolean => {
  if (!isNativeWebView) {
    return true;
  }
  if (!options?.amplitudeAutocaptureEnabled) {
    return true;
  }
  if (options.isComplianceDocument) {
    return true;
  }
  return false;
};
